import type { LoyaltyRewardQuoteResult, LoyaltyRewardSnapshot } from "../gateway/loyalty-redemption-client";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

export type LocalLoyaltyRewardPricing = {
  cartFingerprint: string;
  appliedDiscountAmount: number;
  totalAmount: number;
  roundingAmount: number;
  currencyCode: string;
  quotedAt: string;
};

export type LocalLoyaltyRedemption = {
  cartRequestKey: string;
  redemptionId: string;
  sourceType: "LOYALTY_REWARD";
  sourceInstanceId: string;
  projectionId: string | null;
  customerId: string;
  customerName: string;
  rewardVersionId: string;
  pointCost: number;
  reward: LoyaltyRewardSnapshot;
  reservationState: "RESERVED" | "RELEASE_PENDING";
  validUntil: string | null;
  pricing: LocalLoyaltyRewardPricing | null;
  updatedAt: string;
};

function scopeKey(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function localId(identity: TrustedTerminalIdentity) {
  return `loyalty-redemption:${scopeKey(identity)}`;
}

function isReward(value: unknown): value is LoyaltyRewardSnapshot {
  if (!value || typeof value !== "object") return false;
  const reward = value as Partial<LoyaltyRewardSnapshot>;
  return typeof reward.reward_version_id === "string"
    && typeof reward.name === "string"
    && typeof reward.point_cost === "number"
    && typeof reward.benefit_type === "string"
    && Boolean(reward.benefit_document)
    && Array.isArray(reward.catalog_refs);
}

function normalizePricing(value: unknown): LocalLoyaltyRewardPricing | null {
  if (!value || typeof value !== "object") return null;
  const pricing = value as Partial<LocalLoyaltyRewardPricing>;
  if (
    typeof pricing.cartFingerprint !== "string" || !pricing.cartFingerprint
    || typeof pricing.appliedDiscountAmount !== "number" || !Number.isFinite(pricing.appliedDiscountAmount)
    || typeof pricing.totalAmount !== "number" || !Number.isFinite(pricing.totalAmount)
    || typeof pricing.roundingAmount !== "number" || !Number.isFinite(pricing.roundingAmount)
    || typeof pricing.currencyCode !== "string" || !pricing.currencyCode
    || typeof pricing.quotedAt !== "string"
  ) return null;
  return pricing as LocalLoyaltyRewardPricing;
}

export function pricingFromRewardQuote(cartFingerprint: string, quote: LoyaltyRewardQuoteResult): LocalLoyaltyRewardPricing | null {
  if (!quote.ready || !quote.applied) return null;
  if (
    typeof quote.applied_discount_amount !== "number"
    || typeof quote.total_amount !== "number"
    || typeof quote.rounding_amount !== "number"
    || typeof quote.currency_code !== "string"
  ) return null;
  return {
    cartFingerprint,
    appliedDiscountAmount: quote.applied_discount_amount,
    totalAmount: quote.total_amount,
    roundingAmount: quote.rounding_amount,
    currencyCode: quote.currency_code,
    quotedAt: new Date().toISOString(),
  };
}

export async function readLocalLoyaltyRedemption(
  identity: TrustedTerminalIdentity,
  cartRequestKey?: string | null,
): Promise<LocalLoyaltyRedemption | null> {
  const row = await posRuntimeDb.journal.get(localId(identity));
  if (!row || row.kind !== "LOYALTY_REDEMPTION" || !row.snapshot || typeof row.snapshot !== "object") return null;
  const value = row.snapshot as Partial<LocalLoyaltyRedemption>;
  if (
    typeof value.cartRequestKey !== "string"
    || typeof value.redemptionId !== "string"
    || value.sourceType !== "LOYALTY_REWARD"
    || typeof value.sourceInstanceId !== "string"
    || typeof value.customerId !== "string"
    || typeof value.customerName !== "string"
    || typeof value.rewardVersionId !== "string"
    || typeof value.pointCost !== "number"
    || (value.reservationState !== "RESERVED" && value.reservationState !== "RELEASE_PENDING")
    || !isReward(value.reward)
  ) return null;
  if (cartRequestKey && value.cartRequestKey !== cartRequestKey) return null;
  return {
    ...value,
    projectionId: typeof value.projectionId === "string" ? value.projectionId : null,
    validUntil: typeof value.validUntil === "string" ? value.validUntil : null,
    pricing: normalizePricing(value.pricing),
    updatedAt: typeof value.updatedAt === "string" ? value.updatedAt : row.updatedAt,
  } as LocalLoyaltyRedemption;
}

export async function saveLocalLoyaltyRedemption(
  identity: TrustedTerminalIdentity,
  value: Omit<LocalLoyaltyRedemption, "updatedAt">,
) {
  const now = new Date().toISOString();
  const existing = await posRuntimeDb.journal.get(localId(identity));
  await posRuntimeDb.journal.put({
    localId: localId(identity),
    scopeKey: scopeKey(identity),
    kind: "LOYALTY_REDEMPTION",
    state: value.reservationState,
    requestKey: `loyalty-redemption:${value.redemptionId}`,
    snapshot: { ...value, updatedAt: now },
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  });
  return readLocalLoyaltyRedemption(identity, value.cartRequestKey);
}

export async function saveLocalLoyaltyRewardPricing(
  identity: TrustedTerminalIdentity,
  value: LocalLoyaltyRedemption,
  pricing: LocalLoyaltyRewardPricing,
) {
  return saveLocalLoyaltyRedemption(identity, { ...value, pricing });
}

export async function clearLocalLoyaltyRewardPricing(
  identity: TrustedTerminalIdentity,
  value: LocalLoyaltyRedemption,
) {
  return saveLocalLoyaltyRedemption(identity, { ...value, pricing: null });
}

export async function markLocalLoyaltyRedemptionReleasePending(
  identity: TrustedTerminalIdentity,
  value: LocalLoyaltyRedemption,
) {
  return saveLocalLoyaltyRedemption(identity, { ...value, reservationState: "RELEASE_PENDING" });
}

export async function clearLocalLoyaltyRedemption(
  identity: TrustedTerminalIdentity,
  cartRequestKey?: string | null,
) {
  if (cartRequestKey) {
    const current = await readLocalLoyaltyRedemption(identity, cartRequestKey);
    if (!current) return;
  }
  await posRuntimeDb.journal.delete(localId(identity));
}
